import * as React from 'react';
import { css } from '@emotion/core';
import cx from 'classnames';

export type ItemGroup<T> = {
  header: React.ReactNode;
  leftIcon?: React.FC<{ className: string }>;
  leftIconRenderer?: () => React.ReactNode;
  meta?: React.ReactNode;
  items: T[];
};

export function isItemGroupArray<T>(
  items: T[] | ItemGroup<T>[]
): items is ItemGroup<T>[] {
  if (items.length === 0) {
    return false;
  }
  return (items[0] as ItemGroup<T>).items !== undefined;
}

export type CKOptionGroupsMenuProps<T> = {
  options: ItemGroup<T>[] | T[];
  renderItem: (item: T, index: number) => React.ReactNode;
  stickyGroups?: boolean;
} & React.HTMLAttributes<HTMLDivElement>;

export function CKOptionGroupsMenu<T>(props: CKOptionGroupsMenuProps<T>) {
  const { options, renderItem, stickyGroups, ...rest } = props;

  return (
    <div
      className="ck-menu ck-menu--options pt-0 w-full"
      css={css`
        outline: 0;
      `}
      {...rest}
    >
      {isItemGroupArray<T>(options) ? (
        options.reduce(
          (results, group, sectionIndex) => {
            let style: React.CSSProperties = {
              userSelect: 'none',
            };
            if (group.leftIcon && group.leftIconRenderer) {
              throw new Error(
                'Cannot use leftIcon and leftIconRenderer in the same item group'
              );
            }

            if (group.leftIcon || group.leftIconRenderer) {
              style.marginLeft = '-0.5rem';
            }

            results.sections.push(
              <div key={sectionIndex}>
                <div
                  className={cx(' h-10 px-4 top-0 z-10 flex items-center', {
                    'sticky border-b border-t border-gray-100 bg-gray-0': stickyGroups,
                  })}
                  style={style}
                >
                  {group.leftIcon && (
                    <group.leftIcon className="ck-picker-icon ck-picker-icon--left h-4 w-4" />
                  )}
                  {group.leftIconRenderer && (
                    <span className="ck-picker-icon ck-picker-icon--left">
                      {group.leftIconRenderer()}
                    </span>
                  )}

                  <span
                    className={cx('text-sm flex-grow', {
                      'text-gray-500': !stickyGroups,
                      'font-medium': stickyGroups,
                    })}
                  >
                    {group.header}
                  </span>

                  {group.meta}
                </div>
                <ul className="py-1">
                  {group.items.map(item =>
                    renderItem(item, results.itemIndex++)
                  )}
                </ul>
              </div>
            );

            return results;
          },
          { sections: [] as React.ReactNode[], itemIndex: 0 }
        ).sections
      ) : (
        <div
          className="ck-menu ck-menu--options"
          css={css`
            outline: 0;
          `}
        >
          <ul>{options.map(renderItem)}</ul>
        </div>
      )}
    </div>
  );
}
